import React, { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { transferAggregatorBillingOwner } from '../../services/api';
import type { AggregatorTeamMember } from '../../types';

interface Props {
  members: AggregatorTeamMember[];
  onClose: () => void;
  onSuccess: () => void;
}

const CONFIRM_PHRASE = 'TRANSFER';

export function TransferBillingOwnerDialog({ members, onClose, onSuccess }: Props) {
  const admins = members.filter(m => m.role === 'admin' && !m.revoked_at);
  const [selectedUserId, setSelectedUserId] = useState(admins[0]?.member_user_id ?? '');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = confirmText === CONFIRM_PHRASE && selectedUserId && !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setLoading(true);
    try {
      await transferAggregatorBillingOwner(selectedUserId);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Transfer failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Transfer Billing Ownership</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            You will lose Billing Owner status. This cannot be undone without the new owner's cooperation.
          </p>
        </div>

        {admins.length === 0 ? (
          <p className="text-sm text-gray-600 mb-4">
            There are no Admins to transfer to. Invite an Admin first.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Transfer to</label>
              <select
                value={selectedUserId}
                onChange={e => setSelectedUserId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {admins.map(m => (
                  <option key={m.member_user_id} value={m.member_user_id}>
                    {m.member_name ?? m.member_email}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <strong>{CONFIRM_PHRASE}</strong> to confirm
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
            )}

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                className="flex-1 py-2 px-4 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Transferring…' : 'Transfer Ownership'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
